import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

// Layered mountain silhouette with softer, more natural ridge line
const buildRidgeGeom = (width = 260, height = 34, segments = 200, seed = 1) => {
    const geom = new THREE.PlaneGeometry(width, height, segments, 6);
    const pos = geom.attributes.position;
    for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        // Multiple octaves for more natural ridge
        const ridge =
            Math.sin(x * 0.045 + seed) * 5.2 +
            Math.sin(x * 0.09 + seed * 1.7) * 2.6 +
            Math.sin(x * 0.18 + seed * 2.4) * 1.4 +
            Math.cos(x * 0.28 + seed * 3.1) * 0.7;
        const height01 = Math.max(0, ridge);
        if (y > 0) pos.setY(i, y + height01 * (y / 17));
    }
    geom.computeVertexNormals();
    return geom;
};

export const Terrain = () => {
    const geomA = useMemo(() => buildRidgeGeom(300, 32, 220, 1), []);
    const geomB = useMemo(() => buildRidgeGeom(300, 26, 220, 3.7), []);
    const geomC = useMemo(() => buildRidgeGeom(300, 18, 220, 6.1), []);

    // Ground bump / color noise via canvas
    const groundMap = useMemo(() => {
        const size = 512;
        const c = document.createElement('canvas');
        c.width = size; c.height = size;
        const ctx = c.getContext('2d');
        const img = ctx.createImageData(size, size);
        for (let i = 0; i < img.data.length; i += 4) {
            const n = 150 + Math.random() * 45;
            img.data[i] = n + 12; img.data[i + 1] = n - 22; img.data[i + 2] = n - 55; img.data[i + 3] = 255;
        }
        ctx.putImageData(img, 0, 0);
        const tex = new THREE.CanvasTexture(c);
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(30, 30);
        tex.anisotropy = 8;
        return tex;
    }, []);

    return (
        <group name="terrain">
            {/* Desert floor */}
            <mesh position={[0, -0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
                <planeGeometry args={[500, 500, 1, 1]} />
                <meshLambertMaterial color="#9c7f60" map={groundMap} />
            </mesh>

            {/* Nearer mesa layer (warm) */}
            <mesh position={[0, 1.4, -55]} geometry={geomC}>
                <meshLambertMaterial color="#8a6a48" />
            </mesh>

            {/* Mid Sandia ridge */}
            <mesh position={[0, 2.6, -85]} geometry={geomB}>
                <meshLambertMaterial color="#66607a" />
            </mesh>

            {/* Far Sandia peaks */}
            <mesh position={[0, 4, -120]} geometry={geomA}>
                <meshLambertMaterial color="#3f4260" />
            </mesh>

            {/* Backdrop haze plane */}
            <mesh position={[0, 14, -150]}>
                <planeGeometry args={[600, 40]} />
                <meshBasicMaterial color="#d99f6c" />
            </mesh>
        </group>
    );
};

// Cloud sprites - very subtle
export const Clouds = () => {
    const ref = useRef();
    useFrame(({ clock }) => {
        if (ref.current) ref.current.position.x = Math.sin(clock.elapsedTime * 0.015) * 8;
    });
    return (
        <group ref={ref} position={[0, 20, -75]}>
            {[
                [-40, 2, 0, 12, 1.6],
                [-14, 0, -6, 9, 1.4],
                [10, 4, -3, 14, 1.9],
                [34, 1, 2, 10, 1.5],
                [58, 3, -4, 12, 1.7],
            ].map(([x, y, z, sx, sy], i) => (
                <mesh key={i} position={[x, y, z]} scale={[sx, sy, sx * 0.6]}>
                    <sphereGeometry args={[1, 14, 10]} />
                    <meshBasicMaterial color="#f5e2c4" transparent opacity={0.4} />
                </mesh>
            ))}
        </group>
    );
};
